import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
    Badge,
    Heart,
    Crown,
    Thumb,
    Flag,
    Medal,
    VerifiedTick
} from '$partials/icons/index.js';
import gsap from 'gsap';
import { CustomEase } from 'gsap/CustomEase';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const stickers = [Badge, Heart, Crown, Thumb, Flag, Medal];

gsap.registerPlugin(ScrollTrigger, CustomEase);

const stickerThemes = [
    { 'bg': '#00CCD7', 'icon': '#0B1527' },
    { 'bg': '#FFD800', 'icon': '#082929' },
    { 'bg': '#FC5200', 'icon': '#FFF' },
    { 'bg': '#082929', 'icon': '#FFD800' }
];

export const Stickers = ({ shouldStart = false }) => {
    /** @type {import('$root/types/components/partials/stickers').StickerConfigState} */
    const [stickerConfigs, setStickerConfigs] = useState();
    const containerRef = useRef(null);
    const scrollTLRef = useRef(gsap.timeline({ paused: true }));
    const introTLRef = useRef(gsap.timeline({ paused: true }));

    const randomRange = (/** @type {number} */ min, /** @type {number} */ max) => `${(Math.random() * (max - min) + min).toFixed(1)}%`;

    useEffect(() => {
        if (shouldStart) {
            /** @type {import('$root/types/components/partials/stickers').StickerStyles[]} */
            const stickerPositions = [
                { top: randomRange(5, 15), left: randomRange(15, 27), transform: 'rotate(-45deg)' },
                { top: randomRange(5, 15), right: randomRange(15, 27), transform: 'rotate(15deg)' },
                { top: randomRange(25, 35), left: randomRange(5, 15), transform: 'rotate(15deg)' },
                { top: randomRange(25, 35), right: randomRange(5, 15), transform: 'rotate(-30deg)' },
            ];
            setStickerConfigs(stickers
                // Randomize
                .map(c => ({ v: c, sort: Math.random() })).sort((a, b) => a.sort - b.sort)
                .map(c => c.v)
                // Limit to 4 max
                .slice(0, 4)
                .map((sticker, i) => {
                    const isVerified = i === 2;
                    return {
                        Icon: isVerified ? VerifiedTick : sticker,
                        style: stickerPositions[i],
                        noBackground: isVerified
                    };
                }));
        }
    }, [shouldStart]);

    useLayoutEffect(() => {
        if (stickerConfigs?.length) {

            const scrollTL = scrollTLRef.current;
            const introTL = introTLRef.current;

            // Intro animation
            stickerConfigs.forEach((_, i) => {
                introTL.to(`.sticker-${i + 1} > div`, {
                    scale: 1,
                    duration: 0.6,
                    ease: 'back.out(1.6)',
                    rotation: '+=360'
                }, i === 0 ? '<' : '-=0.35');
            });

            // Play intro animation
            introTL.play();


            // Create scroll trigger
            ScrollTrigger.create({
                trigger: 'main',
                animation: scrollTL,
                start: 'top -5%',
                end: '+=120%',
                scrub: true,
                invalidateOnRefresh: true,
            });

            // Assign endpoint to each sticker
            stickerConfigs.forEach((_, i) => {
                const isPair = i % 2 === 0;

                scrollTL.to(`.sticker-${i+1}`, {
                    duration: 0.4,
                    x: isPair ? '-=400' : '+=400',
                    y: '-=400',
                    rotation: isPair ? '-=250' : '+=250',
                    ease: 'power2.out'
                }, '-=0.375');
            });
        }
    }, [stickerConfigs?.length]);
    return (
        <div
            className='fixed z-40 top-0 left-0 w-dvw h-dvh overflow-clip pointer-events-none'
            ref={containerRef}>
            {(stickerConfigs && stickerConfigs?.length > 0) && (
                <>
                    {stickerConfigs.map((sticker, i) => {
                        return (
                            <div
                                aria-hidden='true'
                                className={`h-64 w-64 hidden md:block absolute z-20 sticker-${i + 1}`}
                                key={i}
                                style={sticker.style}>
                                <div
                                    className={sticker.noBackground ? '' : 'overflow-hidden rounded-full h-full flex items-center justify-center p-20'}
                                    style={{
                                        ...(sticker.noBackground ? {} : { backgroundColor: stickerThemes[i].bg, color: stickerThemes[i].icon }),
                                        transform: 'scale(0)'
                                    }}>
                                    <sticker.Icon
                                        className='w-auto h-full'
                                        {...(sticker.noBackground ? { width: 64, height: 64 } : {})} />
                                </div>
                            </div>
                        );
                    })}
                </>
            )}
        </div>
    );
};
